import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiMobileAppUpdateCheck } from "@tm-food/api-contract";
import { checkMobileAppUpdate } from "@/modules/sys/api/sysApi";
import { nativeMobilePlatform } from "@/platform/nativeBridge";
import { useAuth } from "@/shared/auth/AuthContext";
import { ConfirmationDialog } from "@/shared/ui";

function releasePlatform(os: "android" | "ios"): "Android" | "Ios" {
  return os === "ios" ? "Ios" : "Android";
}

function openableDownloadUrl(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  try {
    const url = new URL(value.trim());
    if (url.protocol === "https:" || url.protocol === "http:" || url.protocol === "itms-services:") {
      return url.href;
    }
  } catch {
    return null;
  }
  return null;
}

export function NativeAppUpdate() {
  const { http } = useAuth();
  const [prompt, setPrompt] = useState<ApiMobileAppUpdateCheck | null>(null);
  const dismissedVersion = useRef<string | null>(null);
  const mobileOs = nativeMobilePlatform();

  const check = useCallback(async () => {
    const os = nativeMobilePlatform();
    if (!os) return;
    const platform = releasePlatform(os);
    try {
      const { readNativeVersionName } = await import("@/platform/capacitor/nativeAppVersion");
      const version = await readNativeVersionName();
      console.info("[app-update] GET /api/v1/sys/mobile-app-releases/check", {
        platform,
        version,
      });
      const result = await checkMobileAppUpdate(http, platform, version);
      console.info("[app-update] response", result);
      if (!result.updateAvailable) {
        setPrompt(null);
        return;
      }
      if (!result.forceUpdate && dismissedVersion.current === result.latestVersionName) return;
      setPrompt(result);
    } catch (error) {
      console.warn("[app-update] request failed", error);
    }
  }, [http]);

  useEffect(() => {
    if (!mobileOs) return;
    void check();
    let remove = () => { };
    let cancelled = false;
    void import("@/platform/capacitor/nativeAppVersion").then(({ subscribeNativeAppResume }) => {
      if (cancelled) return;
      remove = subscribeNativeAppResume(() => {
        void check();
      });
    });
    return () => {
      cancelled = true;
      remove();
    };
  }, [check, mobileOs]);

  if (!mobileOs || !prompt?.updateAvailable) return null;

  const downloadUrl = openableDownloadUrl(prompt.downloadUrl);
  const description =
    prompt.releaseNotes?.trim() ||
    (prompt.latestVersionName ? `Phiên bản ${prompt.latestVersionName}` : undefined);

  return (
    <ConfirmationDialog
      open
      hideCancel={prompt.forceUpdate}
      onOpenChange={(next) => {
        if (next || prompt.forceUpdate) return;
        dismissedVersion.current = prompt.latestVersionName ?? null;
        setPrompt(null);
      }}
      title="Có bản cập nhật"
      description={description}
      confirmLabel="Cập nhật"
      cancelLabel="Để sau"
      onConfirm={() => {
        if (!downloadUrl) return;
        void import("@/platform/capacitor/openExternalUrl")
          .then(({ openExternalUrl }) => openExternalUrl(downloadUrl))
          .catch((error) => {
            console.warn("Open app download", error);
          });
      }}
    />
  );
}
